import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import Bid from '@/models/Bid';
import featureFlagSlugs from '@/constants/featureFlagSlugs';
import eventBus from '@/utilities/EventBus';
import { sendAuctionNotification } from './browserNotification';

const isOnMarketplace = (routeName) => ['Marketplace'].includes(routeName);

async function isOnMarketplaceWithGraphQl(store) {
  const usingGraphQlOnSrp = await store.dispatch('getFlag', featureFlagSlugs.enableGraphqlOnSrp, { root: true });
  if (!usingGraphQlOnSrp) {
    return false;
  }

  return store.state?.route?.name === 'Marketplace';
}

const auctionMessageHandlers = {
  TWO_MIN: async (store, data) => {
    if (await isOnMarketplaceWithGraphQl(store)) return;

    if (data.auction) {
      const auction = await store.dispatch('auctions/getAuction', { auctionId: Number.parseInt(data.auction.id, 10), fromCache: true });
      sendAuctionNotification(auction, 'Ending Soon!', store, 'TWO_MIN');
    }
  },
  DEAL_UPDATE: (store, data) => eventBus.$emit('deal-update', data),
  OUT_BID: async (store, data) => {
    if (await isOnMarketplaceWithGraphQl(store)) return;

    if (data.auction) {
      const auction = await store.dispatch('auctions/getAuction', { auctionId: Number.parseInt(data.auction.id, 10), fromCache: true });
      sendAuctionNotification(auction, 'You\'ve Been Outbid!', store, 'OUT_BID');
    }
  },
  BID: async (store, data) => {
    if (await isOnMarketplaceWithGraphQl(store)) return;

    if (!isEmpty(data)) {
      const auctionId = get(data, 'auction.id', null);
      const amount = get(data, 'auction.current_bid', null);
      const proxy = false;
      const success = get(data, 'auction.success', null);
      const message = get(data, 'message', null);
      const highBidDealerId = get(data, 'auction.high_bidder_dealer_id', null);
      const highBidUserId = get(data, 'auction.high_bidder_id', null);
      const sellerDealerId = get(data, 'auction.seller_dealer_id', null);
      const sellerUserId = get(data, 'auction.seller_id', null);
      const endTime = get(data, 'auction.end_time', null);
      const bidCount = get(data, 'auction.bid_count', null);

      const { name: routeName } = store.state.route;
      if (isOnMarketplace(routeName)) {
        if (store.state.marketplaceAuctions.auctionLanesRefreshing) return;

        const auctionInStore = store.getters['marketplaceAuctions/getAuctionById'](auctionId);

        if (auctionInStore) {
          if (amount > auctionInStore.highBid) {
            await store.dispatch('marketplaceAuctions/handleBidMessage', data);
          }
        }
      } else {
        const bid = new Bid(
          amount,
          auctionId,
          proxy,
          success,
          message,
          highBidDealerId,
          highBidUserId,
          sellerDealerId,
          sellerUserId,
          endTime,
          bidCount,
        );

        await store.dispatch('auctions/handleBidMessage', bid);
      }
    }
  },
  ENDED_UPDATE: async (store, data) => {
    if (await isOnMarketplaceWithGraphQl(store)) return;

    let dataToUse;
    if (data?.message?.auction) {
      dataToUse = data?.message;
    } else if (data?.auction) {
      dataToUse = data;
    }

    if (dataToUse) {
      const { name: routeName } = store.state.route;

      if (isOnMarketplace(routeName)) {
        if (store.state.marketplaceAuctions.auctionLanesRefreshing) return;
      }

      store.dispatch('marketplaceAuctions/handleEndedUpdateMessage', dataToUse);
    }
  },
  END: async (store, data) => {
    if (await isOnMarketplaceWithGraphQl(store)) return;
    if (store.state.marketplaceAuctions.auctionLanesRefreshing) return;

    const { name: routeName } = store.state.route;
    if (isOnMarketplace(routeName)) {
      store.dispatch('marketplaceAuctions/handleEndMessage', data);
    }
  },
  KILLED: async (store, data) => {
    if (await isOnMarketplaceWithGraphQl(store)) return;
    if (store.state.marketplaceAuctions.auctionLanesRefreshing) return;

    const { name: routeName } = store.state.route;
    if (isOnMarketplace(routeName)) {
      store.dispatch('marketplaceAuctions/handleKilledMessage', data);
    }
  },
  NEW: async (store, data) => {
    if (await isOnMarketplaceWithGraphQl(store)) return;
    if (data.auction) {
      const auctionId = Number.parseInt(data.auction.id, 10);
      const dealerIds = store.state.authorization.user.assignedDealershipIds;

      const auction = await store.dispatch('auctions/getAuction', { auctionId });
      let message = 'New Auction Launched!';
      if (!dealerIds.includes(data.auction.seller_dealer_id)) {
        message = 'New Auction Started!';
      }
      sendAuctionNotification(auction, message, store, 'NEW');
    }
  },
};

export default auctionMessageHandlers;
